import {
  CreditCard,
  Calendar,
  ArrowUpDown,
} from "lucide-react";
import Pagination from "../PaginationComp";
import { useMemo } from "react";

// Status configuration - updated for "completed"
const statusConfig = {
  completed: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Completed",
  },
  success: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Success",
  },
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Pending",
  },
  failed: {
    color: "bg-red-100 text-red-800 border-red-200",
    label: "Failed",
  },
  refunded: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Refunded",
  },
  canceled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    label: "Canceled",
  },
};

// Payment method configuration with manual as default
const methodConfig = {
  manual: {
    color: "text-gray-600 bg-gray-100",
    label: "Manual",
    description: "Admin Added",
  },
  upi: {
    color: "text-blue-600 bg-blue-50",
    label: "UPI",
    description: "Unified Payments Interface",
  },
  cash: {
    color: "text-green-600 bg-green-50",
    label: "Cash",
    description: "Cash payment",
  },
  card: {
    color: "text-purple-600 bg-purple-50",
    label: "Card",
    description: "Debit/Credit Card",
  },
  netbanking: {
    color: "text-orange-600 bg-orange-50",
    label: "Net Banking",
    description: "Internet Banking",
  },
};

const PaymentHistoryTab = ({
  payments = [],
  pagination,
  sortOrder,
  onSortChange,
  onPageChange,
  loading = false, // Added loading prop
}) => {
  /* -------- Totals -------- */
  const totals = useMemo(() => {
    return payments.reduce(
      (acc, p) => {
        if (["completed", "success"].includes(p.payment_status)) {
          acc.totalPaid += p.total_amount || 0;
          acc.totalDiscount += p.discount_amount || 0;
          acc.totalOriginal += p.amount || 0;
        }
        return acc;
      },
      { totalPaid: 0, totalDiscount: 0, totalOriginal: 0 },
    );
  }, [payments]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Skeletons for Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-24"></div>
                  <div className="h-8 bg-gray-300 rounded w-20"></div>
                </div>
                <div className="p-3 bg-gray-200 rounded-lg">
                  <div className="w-6 h-6 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Loading Skeleton for Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
          <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
        </div>

        {/* Loading Skeleton for Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-12 bg-gray-200 rounded flex-1"></div>
                  <div className="h-12 bg-gray-200 rounded flex-1"></div>
                  <div className="h-12 bg-gray-200 rounded flex-1"></div>
                  <div className="h-12 bg-gray-200 rounded flex-1"></div>
                  <div className="h-12 bg-gray-200 rounded flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!payments.length) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No Payment History
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Your payment transactions will appear here once you make your first
          payment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center justify-center">
            <div>
              <p className="text-sm text-emerald-700 font-medium">Total Paid</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                ₹{totals.totalPaid.toLocaleString()}
              </p>
              {totals.totalDiscount > 0 && (
                <p className="text-xs text-emerald-600 mt-1">
                  Saved ₹{totals.totalDiscount.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center justify-center">
            <div>
              <p className="text-sm text-blue-700 font-medium">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-gray-800 text-center mt-1">
                {payments.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center justify-center">
            <div>
              <p className="text-sm text-amber-700 font-medium">Completed</p>
              <p className="text-2xl font-bold text-gray-800 text-center mt-1">
                {
                  payments.filter(
                    (p) =>
                      p.payment_status === "completed" ||
                      p.payment_status === "success",
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5">
          <div className="flex items-center justify-center">
            <div>
              <p className="text-sm text-purple-700 font-medium">
                Total Discount
              </p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                ₹{totals.totalDiscount.toLocaleString()}
              </p>
              {totals.totalOriginal > 0 && (
                <p className="text-xs text-purple-600 mt-1">
                  Original ₹{totals.totalOriginal.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div>
          <button
            onClick={() => onSortChange(sortOrder === "desc" ? "asc" : "desc")}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            disabled={loading} // Disable button during loading
          >
            {sortOrder === "desc" ? "Newest First" : "Oldest First"}
            <ArrowUpDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Plan & Transaction
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date & Verification
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => {
                const status =
                  statusConfig[payment.payment_status] || statusConfig.pending;
                const method =
                  methodConfig[payment.payment_method] || methodConfig.manual;

                return (
                  <tr
                    key={payment._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {payment.plan_id?.title || "Unknown Plan"}
                        </p>
                        <div className="text-sm text-gray-500 mt-1 space-y-1">
                          {payment.transaction_id && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">TXN:</span>
                              <code className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                                {payment.transaction_id}
                              </code>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-4 px-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-lg font-bold text-gray-800">
                              ₹
                              {Number(
                                payment.total_amount || 0,
                              ).toLocaleString()}
                            </p>
                            {payment.amount &&
                              payment.amount !== payment.total_amount && (
                                <p className="text-sm text-gray-500 line-through">
                                  ₹{payment.amount.toLocaleString()}
                                </p>
                              )}
                          </div>
                        </div>

                        {/* Discount information */}
                        {(payment.discount_amount > 0 ||
                          payment.coupon_code) && (
                          <div className="text-xs space-y-1">
                            {payment.discount_amount > 0 && (
                              <p className="text-emerald-600 font-medium">
                                -₹{payment.discount_amount} discount
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${method.color}`}>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700 block">
                            {method.label}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="py-4 px-6">
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${status.color}`}
                      >
                        <span className="text-sm font-medium">
                          {status.label}
                        </span>
                      </div>
                    </td>

                    <td className="py-4 px-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-800">
                              {new Date(payment.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </p>
                          </div>
                        </div>

                        {payment.verified_at && (
                          <div className="text-xs text-gray-500 mt-2">
                            <p>
                              Verified:{" "}
                              {new Date(payment.verified_at).toLocaleDateString(
                                "en-IN",
                                {
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          {pagination?.totalPages > 1 && (
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.page}
              onPageChange={onPageChange}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryTab;